import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Pressable, ScrollView, Text } from '@rbilabs/universal-components';
import { checkIfValidTip } from 'features/payment/utils';
import { TextInput as RNTextInput } from 'react-native';
import { dollarsToCents } from 'utils';

import Currency from 'components/currency';
import { TextInput } from 'components/ucl';
import { LogUserErrorSeverity } from 'hooks/log-user-error-message';
import useEffectOnce from 'hooks/use-effect-once';
import { useCartContext } from 'state/cart';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { CURRENCY_PREFIX } from 'state/intl/types';
import { TipAmounts } from 'state/order/hooks/use-cart-tip';
import { getDesignToken, primitive, spacing } from 'styles/constants';
import { isWeb } from 'utils/environment';

const TipList = ScrollView.withConfig({
  keyboardShouldPersistTaps: 'always',
  width: 'full',
  overflow: 'hidden',
  backgroundColor: getDesignToken('background-default'),
  borderColor: primitive.$greyThree,
  borderWidth: 2,
  borderLeftRadius: primitive.$borderRadius,
  borderRightRadius: primitive.$borderRadius,
  padding: 0,
  marginY: '$8',
});

const PressableTip = Pressable.withConfig<{
  $isSelected: boolean;
  $isFirst?: boolean;
  $isLast?: boolean;
  $isTipLocked?: boolean;
}>(p => ({
  borderRightWidth: p.$isLast ? 0 : 1,
  borderRightColor: p.$isLast
    ? 'transparent'
    : p.$isTipLocked
    ? primitive.$disabledTan
    : primitive.$greyThree,
  backgroundColor: p.$isSelected
    ? p.$isTipLocked
      ? primitive.$disabledTan
      : primitive.$fieryRed
    : getDesignToken('background-default'),
  padding: '$2_5',
  borderLeftRadius: p.$isFirst ? 8 : 0,
  borderRightRadius: p.$isLast ? 8 : 0,
}));

const TipText = Text.withConfig<{ $isSelected: boolean; $isTipLocked?: boolean }>(p => ({
  fontWeight: 'bold',
  textAlign: 'center',
  color: p.$isTipLocked
    ? p.$isSelected
      ? primitive.$mayoEggWhite
      : primitive.$disabledTan
    : p.$isSelected
    ? primitive.$mayoEggWhite
    : primitive.$greyOne,
}));

const TipErrorMsg = Box.withConfig({
  marginTop: spacing.$0_5,
  _text: {
    color: getDesignToken('color-error'),
    fontSize: '2xs',
  },
});

const MAX_TIP_LENGTH = 6; // Dont allow number longer than $99.99

type Props = {
  subTotalCents: number;
  showTipPercentage: boolean;
  otherDiscountAmount: number;
  isLoading?: boolean;
  isDisabled: boolean;
};

const DEFAULT_TIP_AMOUNT = '0';

const TipSelector: React.FC<React.PropsWithChildren<Props>> = ({
  subTotalCents,
  otherDiscountAmount,
  showTipPercentage,
  isLoading,
  isDisabled,
}) => {
  const { locale } = useLocale();
  const {
    setTipSelection,
    tipSelection,
    updateTipAmount,
    tipAmount: currentTipAmount,
    isTipLocked,
  } = useCartContext();

  const onTipPercentChange = useCallback(
    (tipPercent: string) => {
      updateTipAmount({
        subTotalCents,
        otherDiscountAmount,
        newTipOption: 'percent',
        newTipAmount: parseFloat(tipPercent),
      });
    },
    [otherDiscountAmount, subTotalCents, updateTipAmount]
  );

  const onTipAmountChange = useCallback(
    (newTipAmount: string) => {
      updateTipAmount({
        subTotalCents,
        otherDiscountAmount,
        newTipOption: 'dollar',
        newTipAmount: parseFloat(newTipAmount),
      });
    },
    [otherDiscountAmount, subTotalCents, updateTipAmount]
  );

  const tip = showTipPercentage ? tipSelection.percentAmount : tipSelection.dollarAmount;
  const [tipString, setTipString] = useState(
    `${CURRENCY_PREFIX[locale]}${tipSelection.otherAmount}`
  );
  const [errorString, setErrorString] = useState('');

  const updateOtherTipAmount = (otherAmount: string) => {
    setTipSelection(selection => ({
      ...selection,
      isOtherSelected: true,
      otherAmount: Number(otherAmount),
    }));
    onTipAmountChange(otherAmount);
  };

  const handleTipAmountChange = (tip: string) => {
    const parsedTip = tip.replace(/[^0-9.]/g, '');
    setTipString(`${CURRENCY_PREFIX[locale]}${parsedTip}`);

    if (!parsedTip) {
      setErrorString(t('tipAmountError'));
      updateOtherTipAmount(DEFAULT_TIP_AMOUNT);
      return;
    }

    // Example: '1.2'
    const tipAmountAsString = parsedTip
      .split('')
      .filter(i => i !== CURRENCY_PREFIX[locale])
      .join('');

    const isValidTip = checkIfValidTip({
      tipAmount: dollarsToCents(Number(tipAmountAsString)),
      subTotalCents,
    });
    if (!isValidTip) {
      setErrorString(t('tipAmountMaxError'));
      updateOtherTipAmount(DEFAULT_TIP_AMOUNT);
      return;
    }

    setErrorString('');
    updateOtherTipAmount(tipAmountAsString);
  };

  const onTipChange = useCallback(
    (amount: string) => {
      if (showTipPercentage) {
        onTipPercentChange(amount);
      } else {
        onTipAmountChange(amount);
      }
    },
    [showTipPercentage, onTipAmountChange, onTipPercentChange]
  );
  const buildRadioProps = (tipAmount: string | number) => {
    const numAmount = tipAmount === 'other' ? 0 : tipAmount;
    return {
      $isSelected: tipSelection.isOtherSelected ? tipAmount === 'other' : tip === tipAmount,
      onPress: () => {
        if (isDisabled || isTipLocked) {
          return;
        }
        setTipSelection(selection => {
          if (tipAmount === 'other') {
            // Reset to default when changing to "Other"
            onTipAmountChange(DEFAULT_TIP_AMOUNT);
            return {
              ...selection,
              isOtherSelected: true,
              otherAmount: 0,
            };
          }
          if (showTipPercentage) {
            return {
              ...selection,
              isOtherSelected: false,
              percentAmount: tipAmount as number,
            };
          }
          return {
            ...selection,
            isOtherSelected: false,
            dollarAmount: tipAmount as number,
          };
        });
        setTipString('');
        setErrorString('');
        if (tipAmount !== 'other') {
          onTipChange(numAmount.toString());
        }
      },
    };
  };

  // Show the error message above the Tip select btns when the tip is > than the subtotal
  // If the user has a invalid tip, keep the error message when the user clicks on the 'other' button until the user start to type in the input
  const showTipSelectBtnError =
    !isLoading && (!tipSelection.isOtherSelected || !tipString) && currentTipAmount > subTotalCents;

  const tipOtherInputRef = useRef<RNTextInput>(null);
  const { isOtherSelected } = tipSelection;
  const previousTipOtherSelected = useRef(isOtherSelected);

  // focus tip input only when "Other" option is first selected
  // prevents keyboard from opening on mobile devices when cart renders Other by default
  useEffect(() => {
    if (!previousTipOtherSelected.current && isOtherSelected && tipOtherInputRef.current) {
      tipOtherInputRef.current.focus();
    }
  }, [isOtherSelected]);

  const tipData = showTipPercentage ? TipAmounts.PERCENT_AMOUNTS : TipAmounts.DOLLAR_AMOUNTS;

  useEffectOnce(() => {
    if (!isWeb) {
      return;
    }
    // extract tip amount from url,
    // this is used to set the tip amount when the user is redirected from cashapp
    const urlParams = new URLSearchParams(window.location.search);
    const urlTipAmount = urlParams.get('tip');
    const isRedirected = urlParams.get('isRedirected') === 'true';
    const urlTipAmountNumber = isNaN(Number(urlTipAmount)) ? -1 : Number(urlTipAmount) / 100;

    if (isRedirected && urlTipAmount !== null) {
      if (urlTipAmountNumber > 0 && tip === urlTipAmountNumber) {
        return;
      }
      updateOtherTipAmount(urlTipAmountNumber.toString());
      setTipString(`${CURRENCY_PREFIX[locale]}${urlTipAmountNumber}`);
    }
  });

  return (
    <Box alignItems="center">
      <TipList
        borderColor={isTipLocked ? primitive.$disabledTan : undefined}
        backgroundColor={getDesignToken('background-input-disabled')}
        showsHorizontalScrollIndicator={false}
        horizontal
        accessibilityLabel="Select Tip"
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {tipData.map((amount, index) => {
          return (
            <PressableTip
              key={index}
              flex={1}
              $isTipLocked={isTipLocked}
              accessibilityLabel={`${amount} tip`}
              {...buildRadioProps(amount)}
              $isFirst={index === 0}
            >
              {showTipPercentage ? (
                <TipText $isSelected={buildRadioProps(amount).$isSelected}>{`${amount}%`}</TipText>
              ) : (
                <Currency
                  amount={amount * 100}
                  isLoading={isLoading}
                  textProps={{
                    fontWeight: 'normal',
                    margin: 0,
                    textAlign: 'center',
                    color: buildRadioProps(amount).$isSelected
                      ? primitive.$mayoEggWhite
                      : isTipLocked
                      ? primitive.$disabledTan
                      : primitive.$greyOne,
                  }}
                />
              )}
            </PressableTip>
          );
        })}
        <PressableTip
          flex={1}
          accessibilityLabel="Other tip"
          {...buildRadioProps('other')}
          $isLast
          $isTipLocked={isTipLocked}
        >
          <TipText $isSelected={tipSelection.isOtherSelected} $isTipLocked={isTipLocked}>
            {t('other')}
          </TipText>
        </PressableTip>
      </TipList>

      {showTipSelectBtnError && (
        <TipErrorMsg accessibilityRole="alert" testID="showTipSelectBtnError">
          {t('tipAmountMaxError')}
        </TipErrorMsg>
      )}
      {isOtherSelected && (
        <TextInput
          testID="customTipInput"
          type="text"
          label={t('tipField')}
          value={tipString}
          errorMessage={errorString}
          logUserErrorSeverity={LogUserErrorSeverity.Recoverable}
          onChangeText={handleTipAmountChange}
          // mask={currencyMask(locale)} // TODO: RN - Implement mask
          maxLength={MAX_TIP_LENGTH}
          ref={tipOtherInputRef}
          keyboardType="numeric"
          returnKeyType="done"
          isDisabled={isDisabled || isTipLocked}
        />
      )}
    </Box>
  );
};

export default TipSelector;
